import React, { useState, useEffect, useRef } from "react";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { useNavigate } from "react-router-dom";
import { ROUTE_CONSTANT } from "../../../routes/constant";
import { toast } from "react-toastify";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaSave,
  FaTimes,
  FaCamera,
  FaArrowLeft,
} from "react-icons/fa";

function EditProfile() {
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    avatar: "",
  });
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const response = await allAPiServicesCall.getProfileDetails(
        {},
        getAuthConfig(),
        navigate
      );
      console.log("Profile API Response:", response);
      if (response?.success && response?.data) {
        // Handle both plain object and Mongoose document (_doc)
        const rawData = response.data;
        const data = rawData._doc || rawData;
        console.log("Extracted data:", data);
        setProfileData({
          fullName: data.fullName || "",
          email: data.email || "",
          mobile: data.mobile || "",
          avatar: data.avatar || "",
        });
        setImagePreview(data.avatar || "");
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handleOnSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!profileData.fullName?.trim()) {
      toast.error("Full name is required");
      return;
    }
    if (!profileData.email?.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!profileData.mobile?.trim()) {
      toast.error("Mobile number is required");
      return;
    }

    setSaving(true);
    try {
      // Note: editProfile already handles success toast and navigation
      await allAPiServicesCall.editProfile(
        {
          fullName: profileData.fullName,
          email: profileData.email,
          mobile: profileData.mobile,
        },
        getAuthConfig(),
        navigate
      );
    } catch (error) {
      console.error("Error updating profile:", error);
      // Error is already handled by apiServices
    } finally {
      setSaving(false);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (event) => {
    event.preventDefault();
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPG, PNG, GIF, WEBP)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      const objectURL = URL.createObjectURL(file);
      setImagePreview(objectURL);
      await uploadImageToServer(file);
    }
  };

  const uploadImageToServer = async (file) => {
    if (!file) {
      return console.log("no image selected");
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      // Note: editProfilePicure already handles success toast and navigation
      await allAPiServicesCall.editProfilePicure(
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
            "Content-Type": "multipart/form-data",
          },
        },
        navigate
      );
      // If we reach here without navigation, update local state
      setProfileData({ ...profileData, avatar: imagePreview });
    } catch (error) {
      // Only show error if it's an actual error (not navigation abort)
      if (error?.name !== 'AbortError' && error?.message !== 'Navigation aborted') {
        console.error(error);
        toast.error("Failed to upload image");
      }
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "AD";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
      padding: "40px 20px",
    },
    wrapper: {
      maxWidth: "900px",
      margin: "0 auto",
    },
    backButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "12px 24px",
      background: "white",
      border: "none",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#64748b",
      cursor: "pointer",
      marginBottom: "24px",
      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
    },
    headerCard: {
      background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
      borderRadius: "24px",
      padding: "40px",
      marginBottom: "24px",
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
    },
    headerPattern: {
      position: "absolute",
      top: 0,
      right: 0,
      width: "300px",
      height: "300px",
      background:
        "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
      borderRadius: "50%",
      transform: "translate(30%, -30%)",
    },
    headerContent: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      position: "relative",
      zIndex: 1,
    },
    avatarContainer: {
      position: "relative",
      marginBottom: "20px",
      cursor: "pointer",
    },
    avatar: {
      width: "140px",
      height: "140px",
      borderRadius: "50%",
      border: "4px solid rgba(255, 255, 255, 0.2)",
      objectFit: "cover",
      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
      transition: "all 0.3s ease",
    },
    avatarPlaceholder: {
      width: "140px",
      height: "140px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #0031E2 0%, #1444F0 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "48px",
      fontWeight: "700",
      color: "white",
      border: "4px solid rgba(255, 255, 255, 0.2)",
      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
    },
    avatarOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: "50%",
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0,
      transition: "opacity 0.3s ease",
    },
    cameraIcon: {
      color: "white",
      fontSize: "32px",
    },
    uploadHint: {
      fontSize: "14px",
      color: "rgba(255, 255, 255, 0.7)",
      marginTop: "12px",
    },
    pageTitle: {
      fontSize: "28px",
      fontWeight: "700",
      color: "white",
      margin: "0",
    },
    formCard: {
      background: "white",
      borderRadius: "20px",
      padding: "32px",
      marginBottom: "24px",
      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
    },
    sectionTitle: {
      fontSize: "20px",
      fontWeight: "700",
      color: "#1a1a2e",
      marginBottom: "24px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    sectionIcon: {
      width: "40px",
      height: "40px",
      background: "linear-gradient(135deg, #0031E2 0%, #1444F0 100%)",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
    },
    formGroup: {
      marginBottom: "24px",
    },
    label: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#374151",
      marginBottom: "8px",
    },
    labelIcon: {
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
    },
    input: {
      width: "100%",
      padding: "16px 20px",
      fontSize: "16px",
      border: "2px solid #e2e8f0",
      borderRadius: "12px",
      outline: "none",
      transition: "all 0.3s ease",
      backgroundColor: "#f8fafc",
    },
    inputFocus: {
      borderColor: "#0031E2",
      backgroundColor: "white",
      boxShadow: "0 0 0 4px rgba(0, 49, 226, 0.1)",
    },
    inputDisabled: {
      backgroundColor: "#f1f5f9",
      color: "#94a3b8",
      cursor: "not-allowed",
    },
    buttonGroup: {
      display: "flex",
      gap: "16px",
      flexWrap: "wrap",
    },
    button: {
      flex: "1",
      minWidth: "180px",
      padding: "16px 32px",
      borderRadius: "14px",
      fontSize: "16px",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      transition: "all 0.3s ease",
    },
    primaryButton: {
      background: "linear-gradient(135deg, #0031E2 0%, #1444F0 100%)",
      color: "white",
      boxShadow: "0 10px 30px rgba(0, 49, 226, 0.4)",
    },
    secondaryButton: {
      background: "white",
      color: "#64748b",
      border: "2px solid #e2e8f0",
      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)",
    },
    disabledButton: {
      opacity: 0.7,
      cursor: "not-allowed",
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
    },
    spinner: {
      width: "60px",
      height: "60px",
      border: "4px solid #e2e8f0",
      borderTopColor: "#0031E2",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    loadingText: {
      marginTop: "20px",
      fontSize: "16px",
      color: "#64748b",
      fontWeight: "500",
    },
  };

  const spinnerKeyframes = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;

  if (loading) {
    return (
      <CommonLayout>
        <style>{spinnerKeyframes}</style>
        <div style={styles.container}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Loading profile...</p>
          </div>
        </div>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout>
      <style>{spinnerKeyframes}</style>
      <div style={styles.container}>
        <div style={styles.wrapper}>
          {/* Back Button */}
          <button
            style={styles.backButton}
            onClick={() => navigate(ROUTE_CONSTANT.AUTH.SHOW_PROFILE)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateX(-4px)";
              e.currentTarget.style.color = "#1a1a2e";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateX(0)";
              e.currentTarget.style.color = "#64748b";
            }}
          >
            <FaArrowLeft /> Back to Profile
          </button>

          {/* Header with Avatar */}
          <div style={styles.headerCard}>
            <div style={styles.headerPattern}></div>
            <div style={styles.headerContent}>
              <div
                style={styles.avatarContainer}
                onClick={handleImageClick}
                onMouseEnter={(e) => {
                  const overlay = e.currentTarget.querySelector(".avatar-overlay");
                  if (overlay) overlay.style.opacity = "1";
                }}
                onMouseLeave={(e) => {
                  const overlay = e.currentTarget.querySelector(".avatar-overlay");
                  if (overlay) overlay.style.opacity = "0";
                }}
              >
                {imagePreview || profileData?.avatar ? (
                  <img
                    src={imagePreview || profileData.avatar}
                    alt="Profile"
                    style={styles.avatar}
                  />
                ) : (
                  <div style={styles.avatarPlaceholder}>
                    {getInitials(profileData?.fullName)}
                  </div>
                )}
                <div className="avatar-overlay" style={styles.avatarOverlay}>
                  <FaCamera style={styles.cameraIcon} />
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
              </div>
              <p style={styles.uploadHint}>Click to change profile picture</p>
              <h1 style={styles.pageTitle}>Edit Profile</h1>
            </div>
          </div>

          {/* Form Card */}
          <div style={styles.formCard}>
            <div style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <FaUser />
              </div>
              Personal Information
            </div>

            <form onSubmit={handleOnSubmit}>
              {/* Full Name */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <div
                    style={{
                      ...styles.labelIcon,
                      background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                      color: "white",
                    }}
                  >
                    <FaUser />
                  </div>
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={profileData.fullName || ""}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  style={styles.input}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#0031E2";
                    e.target.style.backgroundColor = "white";
                    e.target.style.boxShadow = "0 0 0 4px rgba(0, 49, 226, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.backgroundColor = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                  required
                />
              </div>

              {/* Email */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <div
                    style={{
                      ...styles.labelIcon,
                      background: "linear-gradient(135deg, #1DB954 0%, #17a34a 100%)",
                      color: "white",
                    }}
                  >
                    <FaEnvelope />
                  </div>
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email || ""}
                  readOnly
                  style={{ ...styles.input, ...styles.inputDisabled }}
                  title="Email cannot be changed"
                />
                <small style={{ color: "#94a3b8", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  Email address cannot be changed
                </small>
              </div>

              {/* Mobile */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <div
                    style={{
                      ...styles.labelIcon,
                      background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "white",
                    }}
                  >
                    <FaPhone />
                  </div>
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={profileData.mobile || ""}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  style={styles.input}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#0031E2";
                    e.target.style.backgroundColor = "white";
                    e.target.style.boxShadow = "0 0 0 4px rgba(0, 49, 226, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.backgroundColor = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                  required
                />
              </div>

              {/* Buttons */}
              <div style={styles.buttonGroup}>
                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    ...(saving ? styles.disabledButton : {}),
                  }}
                  disabled={saving}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 15px 40px rgba(0, 49, 226, 0.5)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 10px 30px rgba(0, 49, 226, 0.4)";
                  }}
                >
                  {saving ? (
                    <>
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTopColor: "white",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave /> Save Changes
                    </>
                  )}
                </button>
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={() => navigate(ROUTE_CONSTANT.AUTH.SHOW_PROFILE)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.boxShadow =
                      "0 10px 30px rgba(0, 0, 0, 0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(0, 0, 0, 0.05)";
                  }}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </CommonLayout>
  );
}

export default EditProfile;
